// src/components/opportunity/PricingCostEntrySection.tsx
//
// Merged base amount + format calc lines for the sales pricing form.
// Each manager calc line carries a per-revision $/% toggle (mirrors the
// deposit box) so sales can pin a percent line to exact dollars after a
// signed contract changes — with a required deviation note.

import { linesForPlacement, effectiveCalcLineMode } from "@/lib/opportunity/formatCalcEval";
import type { FormatCalcAmountMode, FormatCalcLine, FormatCalcLineDef } from "@/lib/opportunity/formatCalcLineTypes";
import type { OpportunityPricingFormState } from "@/components/opportunity/OpportunityPricingForm";

export type PricingCostEntrySectionProps = {
  baseAmountLabel: string;
  lines: FormatCalcLine[];
  form: OpportunityPricingFormState;
  setForm: (next: OpportunityPricingFormState) => void;
};

export function PricingCostEntrySection(props: PricingCostEntrySectionProps) {
  const { baseAmountLabel, lines, form, setForm } = props;
  const before = linesForPlacement(lines, "before_subtotal");
  const after = linesForPlacement(lines, "after_subtotal");

  function updateLineValue(lineId: string, value: string) {
    setForm({
      ...form,
      formatCalcValues: { ...form.formatCalcValues, [lineId]: value },
    });
  }

  function updateLineMode(line: FormatCalcLineDef, mode: FormatCalcAmountMode) {
    const modes = { ...form.formatCalcModes };
    const notes = { ...form.formatCalcDeviationNotes };
    if (mode === line.amount_mode) {
      // Back to the manager default — drop the override and its note.
      delete modes[line.id];
      delete notes[line.id];
    } else {
      modes[line.id] = mode;
    }
    setForm({ ...form, formatCalcModes: modes, formatCalcDeviationNotes: notes });
  }

  function updateLineNote(lineId: string, note: string) {
    setForm({
      ...form,
      formatCalcDeviationNotes: { ...form.formatCalcDeviationNotes, [lineId]: note },
    });
  }

  function renderLine(line: FormatCalcLineDef) {
    return (
      <CalcLineRow
        key={line.id}
        line={line}
        mode={effectiveCalcLineMode(line, form.formatCalcModes)}
        value={form.formatCalcValues[line.id] ?? ""}
        note={form.formatCalcDeviationNotes[line.id] ?? ""}
        onChange={(v) => updateLineValue(line.id, v)}
        onModeChange={(m) => updateLineMode(line, m)}
        onNoteChange={(n) => updateLineNote(line.id, n)}
      />
    );
  }

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900 space-y-2">
      <CostRow
        label={baseAmountLabel}
        value={form.subtotalInput}
        onChange={(v) => setForm({ ...form, subtotalInput: v })}
        placeholder="0.00"
      />
      {before.map(renderLine)}
      {after.map(renderLine)}
    </section>
  );
}

function CostRow(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 items-center">
      <span className="text-sm text-gray-800 dark:text-zinc-100 truncate">{props.label}</span>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        inputMode="decimal"
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
      />
    </div>
  );
}

function CalcLineRow(props: {
  line: FormatCalcLineDef;
  mode: FormatCalcAmountMode;
  value: string;
  note: string;
  onChange: (value: string) => void;
  onModeChange: (mode: FormatCalcAmountMode) => void;
  onNoteChange: (note: string) => void;
}) {
  const { line, mode } = props;
  const overriddenToDollars = line.amount_mode === "percent" && mode === "fixed";
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-2 items-center">
        <span className="text-sm text-gray-800 dark:text-zinc-100 truncate">{line.label}</span>
        <div className="flex items-center gap-1.5">
          <input
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder={mode === "percent" ? "0" : "0.00"}
            inputMode="decimal"
            className="w-full min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
          />
          <CalcLineModeToggle
            value={mode}
            onChange={props.onModeChange}
            ariaLabel={`${line.label} amount mode`}
          />
        </div>
      </div>
      {overriddenToDollars ? (
        <div className="pl-1">
          <p className="text-[11px] text-amber-700 dark:text-amber-300">
            Dollar override — the manager % rule is not applied to this line.
          </p>
          <textarea
            value={props.note}
            onChange={(e) => props.onNoteChange(e.target.value)}
            rows={2}
            placeholder={`Why is "${line.label}" a manual dollar amount? (required)`}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
          />
        </div>
      ) : null}
    </div>
  );
}

function CalcLineModeToggle(props: {
  value: FormatCalcAmountMode;
  onChange: (next: FormatCalcAmountMode) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={props.ariaLabel}
      className="inline-flex shrink-0 rounded-lg border border-gray-200 overflow-hidden text-xs dark:border-zinc-700"
    >
      {(["fixed", "percent"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="radio"
          aria-checked={props.value === mode}
          onClick={() => props.onChange(mode)}
          className={
            "px-2 py-1.5 " +
            (props.value === mode
              ? "bg-indigo-600 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800")
          }
        >
          {mode === "fixed" ? "$" : "%"}
        </button>
      ))}
    </div>
  );
}
